import {AlertTriangle, CalendarCheck, CalendarDays} from "lucide-react";
import {Link} from "react-router-dom";

import {Badge} from "@/components/ui/badge";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {cn} from "@/lib/utils";

import type {DashboardShifts} from "../types";

type ShiftsCardProps = {
  shifts: DashboardShifts;
};

export function ShiftsCard({shifts}: ShiftsCardProps) {
  const totalInPeriod = toCount(shifts.total_in_period);
  const workedInPeriod = toCount(shifts.worked_in_period);
  const missedCount = toCount(shifts.missed_in_period?.count);
  const missedItems = shifts.missed_in_period?.items ?? [];
  const hasMissed = missedCount > 0;
  const workRate =
    totalInPeriod > 0
      ? (workedInPeriod / totalInPeriod) * 100
      : 0;

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-foreground text-sm font-semibold">Shifts</CardTitle>
        <Badge variant="outline" className="font-normal">
          Company-wide
        </Badge>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={CalendarDays} label="Total" value={totalInPeriod} />
          <Stat
            icon={CalendarCheck}
            label="Worked"
            value={workedInPeriod}
            footer={`${workRate.toFixed(0)}% completion`}
          />
        </div>

        <div
          className={cn(
            "flex flex-col gap-2 rounded-md border p-3",
            hasMissed
              ? "border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950"
              : "border-border bg-muted/30",
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                "flex items-center gap-2 text-sm font-medium",
                hasMissed ? "text-rose-700 dark:text-rose-300" : "text-muted-foreground",
              )}
            >
              <AlertTriangle className="size-4" />
              Missed shifts
            </span>
            <span
              className={cn(
                "tabular-nums text-lg font-semibold",
                hasMissed ? "text-rose-700 dark:text-rose-300" : "text-muted-foreground",
              )}
            >
              {missedCount.toLocaleString()}
            </span>
          </div>

          {hasMissed && missedItems.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {missedItems.slice(0, 4).map((item, index) => {
                const shiftLabel = item.user_name?.trim()
                  ? item.user_name
                  : `Shift #${item.shift_id}`;
                return (
                  <li
                    key={`${item.shift_id || "shift"}-${index}`}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <Link
                      to={`/shifts/${item.shift_id}`}
                      className="text-foreground hover:text-primary min-w-0 truncate font-medium"
                    >
                      {shiftLabel}
                      {item.property_name ? (
                        <span className="text-muted-foreground"> · {item.property_name}</span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
              {missedItems.length > 4 ? (
                <li className="text-muted-foreground text-xs">
                  +{missedItems.length - 4} more
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  footer,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  footer?: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-md border p-3">
      <span className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <Icon className="size-3.5" />
        {label}
      </span>
      <span className="text-foreground text-xl font-semibold tabular-nums">
        {value.toLocaleString()}
      </span>
      {footer ? <span className="text-muted-foreground text-xs">{footer}</span> : null}
    </div>
  );
}

function toCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value === "object") {
    const maybeCount = (value as {count?: unknown}).count;
    if (typeof maybeCount === "number" && Number.isFinite(maybeCount)) return maybeCount;
  }
  return 0;
}
